/**
 * AuditLogs Page
 * Admin page for viewing authentication audit logs with filtering
 */

import { useState, useEffect, useCallback } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { auditApi } from '../../services/auditApi';
import { AuditLogTable, AuditLogFilters } from '../../components/audit';
import { Modal, Button } from '../../components/ui';
import { pageStyles, cardStyles } from '../../lib/styles';
import type { AuditLogEntry, Pagination, EventTypeInfo, AuditLogFilterParams } from '@leal-crm/shared';

export function AuditLogsPage() {
  const { user } = useAuth();
  const { error: showError } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    pageSize: 25,
    totalItems: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEntry, setSelectedEntry] = useState<AuditLogEntry | null>(null);
  const [eventTypes, setEventTypes] = useState<EventTypeInfo[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  // Parse initial filters from URL
  const getFiltersFromUrl = useCallback((): AuditLogFilterParams => {
    const filters: AuditLogFilterParams = {
      page: parseInt(searchParams.get('page') || '1', 10),
      pageSize: 25,
    };

    const startDate = searchParams.get('startDate');
    const endDate = searchParams.get('endDate');
    const eventTypesParam = searchParams.get('eventTypes');
    const search = searchParams.get('search');
    const entityType = searchParams.get('entityType');

    if (startDate) filters.startDate = startDate;
    if (endDate) filters.endDate = endDate;
    if (eventTypesParam) filters.eventTypes = eventTypesParam.split(',');
    if (search) filters.search = search;
    if (entityType && entityType !== 'all') filters.entityType = entityType as AuditLogFilterParams['entityType'];

    return filters;
  }, [searchParams]);

  // Admin-only access
  if (!user || user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  // Sync filters to URL
  const updateUrl = (filters: AuditLogFilterParams) => {
    const params = new URLSearchParams();

    if (filters.page && filters.page > 1) params.set('page', String(filters.page));
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);
    if (filters.eventTypes && filters.eventTypes.length > 0) {
      params.set('eventTypes', filters.eventTypes.join(','));
    }
    if (filters.search) params.set('search', filters.search);
    if (filters.entityType) params.set('entityType', filters.entityType);

    setSearchParams(params, { replace: true });
  };

  const fetchLogs = async (filters: AuditLogFilterParams) => {
    try {
      setIsLoading(true);
      const result = await auditApi.getLogs(filters);
      setLogs(result.logs);
      setPagination(result.pagination);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al cargar registros';
      showError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEventTypes = async () => {
    try {
      const result = await auditApi.getEventTypes();
      setEventTypes(result.eventTypes);
    } catch {
      // Silently fail - filters will work without event type names
    }
  };

  // Initial load
  useEffect(() => {
    fetchEventTypes();
    fetchLogs(getFiltersFromUrl());
  }, []);

  // Handle filter changes
  const handleFilterChange = (filters: AuditLogFilterParams) => {
    updateUrl(filters);
    fetchLogs(filters);
  };

  const handlePageChange = (page: number) => {
    const currentFilters = getFiltersFromUrl();
    const newFilters = { ...currentFilters, page };
    updateUrl(newFilters);
    fetchLogs(newFilters);
  };

  const handleViewDetails = (entry: AuditLogEntry) => {
    setSelectedEntry(entry);
  };

  const closeModal = () => {
    setSelectedEntry(null);
  };

  /**
   * Format timestamp to readable format
   */
  const formatDateTime = (timestampSeconds: number): string => {
    const date = new Date(timestampSeconds * 1000);
    return date.toLocaleString();
  };

  // Check if any filters are active
  const currentFilters = getFiltersFromUrl();
  const hasActiveFilters =
    currentFilters.startDate ||
    currentFilters.endDate ||
    (currentFilters.eventTypes && currentFilters.eventTypes.length > 0) ||
    currentFilters.search ||
    currentFilters.entityType;

  return (
    <div className={pageStyles.container}>
      {/* Header */}
      <div className={pageStyles.header}>
        <div>
          <h1 className={pageStyles.title}>Registros de AuditorÃ­a</h1>
          <p className={pageStyles.subtitle}>
            Historial de eventos de autenticaciÃ³n y seguridad
          </p>
        </div>
        <Button
          variant={showFilters ? 'default' : 'secondary'}
          onClick={() => setShowFilters(!showFilters)}
        >
          {showFilters ? 'Ocultar filtros' : 'Mostrar filtros'}
          {hasActiveFilters && (
            <span className="ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-xs text-white">
              !
            </span>
          )}
        </Button>
      </div>

      {/* Filters */}
      {showFilters && (
        <AuditLogFilters
          eventTypes={eventTypes}
          onFilterChange={handleFilterChange}
          initialFilters={currentFilters}
          isLoading={isLoading}
        />
      )}

      {/* Active filters indicator */}
      {hasActiveFilters && !showFilters && (
        <div className={cardStyles.container}>
          <div className={cardStyles.content}>
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
              <span>Filtros activos:</span>
              {currentFilters.search && (
                <span className="bg-gray-100 rounded px-2 py-0.5">
                  Busqueda: "{currentFilters.search}"
                </span>
              )}
              {currentFilters.startDate && (
                <span className="bg-gray-100 rounded px-2 py-0.5">
                  Desde: {currentFilters.startDate}
                </span>
              )}
              {currentFilters.endDate && (
                <span className="bg-gray-100 rounded px-2 py-0.5">
                  Hasta: {currentFilters.endDate}
                </span>
              )}
              {currentFilters.eventTypes && currentFilters.eventTypes.length > 0 && (
                <span className="bg-gray-100 rounded px-2 py-0.5">
                  {currentFilters.eventTypes.length} tipos de evento
                </span>
              )}
              {currentFilters.entityType && (
                <span className="bg-gray-100 rounded px-2 py-0.5">
                  Tipo: {currentFilters.entityType}
                </span>
              )}
              <button
                onClick={() => setShowFilters(true)}
                className="text-blue-600 hover:underline"
              >
                Editar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <AuditLogTable
        logs={logs}
        pagination={pagination}
        isLoading={isLoading}
        onViewDetails={handleViewDetails}
        onPageChange={handlePageChange}
      />

      {/* Details Modal */}
      <Modal
        isOpen={!!selectedEntry}
        onClose={closeModal}
        title="Detalles del Evento"
        size="md"
      >
        {selectedEntry && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase">
                  Fecha/Hora
                </label>
                <p className="mt-1 text-sm text-gray-900">
                  {formatDateTime(selectedEntry.createdAt)}
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase">
                  Evento
                </label>
                <p className="mt-1 text-sm text-gray-900">
                  {selectedEntry.actionDisplayName || selectedEntry.action}
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase">
                  Usuario
                </label>
                <p className="mt-1 text-sm text-gray-900">
                  {selectedEntry.actorType === 'system' || selectedEntry.actorId === 'anonymous'
                    ? 'Sistema'
                    : selectedEntry.actorName || selectedEntry.actorEmail || selectedEntry.actorId}
                </p>
                {selectedEntry.actorType !== 'system' && selectedEntry.actorId !== 'anonymous' && selectedEntry.actorEmail && selectedEntry.actorName && (
                  <p className="text-xs text-gray-500">{selectedEntry.actorEmail}</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase">
                  DirecciÃ³n IP
                </label>
                <p className="mt-1 text-sm text-gray-900">
                  {selectedEntry.ipAddress || '-'}
                </p>
              </div>
            </div>

            {selectedEntry.changes && Object.keys(selectedEntry.changes).length > 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase mb-2">
                  Detalles Adicionales
                </label>
                <pre className="bg-gray-50 rounded-md p-3 text-xs text-gray-700 overflow-x-auto">
                  {JSON.stringify(selectedEntry.changes, null, 2)}
                </pre>
              </div>
            )}

            <div className="pt-4 border-t">
              <p className="text-xs text-gray-400">
                ID: {selectedEntry.id}
              </p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

